import {useCallback, useMemo, useState} from 'react'
import type {ChatInputAnswer, ChatInputRequest} from '@microsoft/agent-host-protocol'
import {getInputRequests, normalizeInputRequest, optionIdForLabel} from '@github-ui/ahp-relay/ahp-session-selectors'
import {
  selectedAnswer,
  selectedManyAnswer,
  textAnswer,
  type BuildInputCompletedActionParams,
} from '@github-ui/ahp-relay/ahp-input-actions'
import {dispatchRelayInputCompleted} from '@github-ui/ahp-relay/relay-registry'
import {useAhpSessionState} from '../ahp/use-ahp-session-state'
import type {NormalizedPrompt} from '../components/session-logs/prompt-log-utils'
import {COMMAND_TYPES} from '../constants/command-types'
import type {
  CommandType,
  AskUserArguments,
  ElicitationAskUserArguments,
  RequestedSchema,
  RequestedSchemaProperty,
  SimpleAskUserArguments,
} from '../types/session'

// AHP question kinds are const enums — compare wire literals.
const QUESTION_KIND_SINGLE_SELECT = 'single-select'
const QUESTION_KIND_MULTI_SELECT = 'multi-select'

/** The shape SimpleAskUserPromptContent sends for an ask_user response. */
interface AskUserResponsePayload {
  answer: string
  wasFreeform?: boolean
  dismissed?: boolean
}

/** The shape ElicitationAskUserPromptContent sends for an elicitation response. */
interface ElicitationResponsePayload {
  action: 'accept' | 'decline'
  content?: Record<string, string | string[]>
}

export interface UseAhpPromptStateOptions {
  /** Task id the shared relay is keyed by. */
  taskId: string | undefined
  /** MC environment the session is bound to. */
  environmentId: string | undefined
  /** Mission Control session id the relay observes. */
  sessionId: string | undefined
  /** When false the hook is inert (no relay acquisition, no pending prompt). */
  enabled: boolean
}

export interface AhpPromptState {
  /** The current pending prompt, or null if none. */
  pendingPrompt: NormalizedPrompt | null
  /** Zero-based index of the current pending prompt. */
  pendingPromptIndex: number
  /** Total number of unresolved prompts. */
  pendingPromptCount: number
  /** True before the first relay snapshot arrives. */
  isLoading: boolean
  /** Not applicable for AHP prompts (the relay owns the lifecycle). */
  isExpired: boolean
  /** Optimistically resolve a request id so it hides before the server confirms. */
  markPromptResolved: (requestId: string) => void
  /** Roll back an optimistic resolution so the prompt re-appears on failure. */
  unmarkPromptResolved: (requestId: string) => void
  /** Dismiss the current prompt client-side. */
  dismissPrompt: () => void
  /** Select the previous unresolved prompt, if one exists. */
  goToPreviousPrompt: () => void
  /** Select the next unresolved prompt, if one exists. */
  goToNextPrompt: () => void
  /**
   * Answer the pending prompt by dispatching `chat/inputCompleted` over the
   * relay. Accepts the same `(toolCallId, response, commandType)` payload the
   * shared `PromptInput` components emit, so the composer wiring is identical to
   * the events/logs path.
   */
  respond: (toolCallId: string, response: unknown, commandType: CommandType) => void
}

/**
 * Build an elicitation schema from a multi-question AHP request so the shared
 * {@link ElicitationAskUserDisplay} can render it. Each question becomes one
 * schema property; select questions expose their options as `enum` +
 * `enumLabels`, and multi-select questions are typed as arrays.
 */
function toRequestedSchema(request: ChatInputRequest): RequestedSchema {
  const properties: Record<string, RequestedSchemaProperty> = {}
  const required: string[] = []

  for (const question of request.questions ?? []) {
    const isSelect = question.kind === QUESTION_KIND_SINGLE_SELECT || question.kind === QUESTION_KIND_MULTI_SELECT
    const options = isSelect ? ((question as {options?: Array<{id: string; label: string}>}).options ?? []) : []
    const enumLabels: Record<string, string> = {}
    for (const option of options) enumLabels[option.id] = option.label

    properties[question.id] = {
      title: question.title ?? question.message,
      description: question.message,
      ...(question.kind === QUESTION_KIND_MULTI_SELECT ? {type: 'array', multiSelect: true} : {}),
      ...(options.length > 0 ? {enum: options.map(o => o.id), enumLabels} : {}),
    }
    if (question.required) required.push(question.id)
  }

  return {properties, ...(required.length > 0 ? {required} : {})}
}

/**
 * Map an AHP {@link ChatInputRequest} onto the same {@link NormalizedPrompt}
 * shape the composer already renders for CLI/events prompts, so the existing
 * `PromptInput` -> `UserChoiceCard` components can be reused unchanged. Returns
 * `null` for requests with no questions.
 */
export function ahpInputRequestToPrompt(request: ChatInputRequest): NormalizedPrompt | null {
  const normalized = normalizeInputRequest(request)
  if (!normalized) return null

  const args: AskUserArguments = normalized.isElicitation
    ? ({
        message: normalized.question,
        requestedSchema: toRequestedSchema(request),
      } satisfies ElicitationAskUserArguments)
    : ({
        question: normalized.question,
        choices: normalized.choices,
        allowFreeform: normalized.allowFreeform,
      } satisfies SimpleAskUserArguments)

  return {toolCallId: request.id, toolName: 'ask_user', arguments: args}
}

function firstQuestionKind(request: ChatInputRequest): string | undefined {
  return (request.questions ?? [])[0]?.kind
}

/**
 * Translate a composer `onRespond` payload into `chat/inputCompleted` params for
 * a given AHP request. Pure and exported for unit testing.
 *
 * - ask_user choice -> accept with a `selected` answer (label mapped to option id)
 * - ask_user freeform -> accept with the freeform text (as `text` or `selected` freeform)
 * - ask_user skip/dismiss -> cancel
 * - elicitation accept/decline -> accept (answers built from content) / decline
 */
export function ahpCompletionFromResponse(
  request: ChatInputRequest,
  response: unknown,
  commandType: CommandType,
): BuildInputCompletedActionParams | null {
  const requestId = request.id

  if (commandType === COMMAND_TYPES.ASK_USER_RESPONSE) {
    const payload = response as AskUserResponsePayload
    if (payload.dismissed) return {requestId, response: 'cancel'}

    const questionId = (request.questions ?? [])[0]?.id
    if (!questionId) return null
    const isTextQuestion = firstQuestionKind(request) === 'text'

    if (payload.wasFreeform) {
      const answer: ChatInputAnswer = isTextQuestion ? textAnswer(payload.answer) : selectedAnswer('', [payload.answer])
      return {requestId, response: 'accept', answers: {[questionId]: answer}}
    }

    const answer: ChatInputAnswer = isTextQuestion
      ? textAnswer(payload.answer)
      : selectedAnswer(optionIdForLabel(request, questionId, payload.answer) ?? payload.answer)
    return {requestId, response: 'accept', answers: {[questionId]: answer}}
  }

  if (commandType === COMMAND_TYPES.ELICITATION_RESPONSE) {
    const payload = response as ElicitationResponsePayload
    if (payload.action === 'decline') return {requestId, response: 'decline'}

    const kindById = new Map((request.questions ?? []).map(q => [q.id, q.kind]))
    const answers: Record<string, ChatInputAnswer> = {}
    for (const [questionId, value] of Object.entries(payload.content ?? {})) {
      const kind = kindById.get(questionId)
      if (Array.isArray(value)) {
        // Multi-select: the values are selected option ids.
        answers[questionId] = selectedManyAnswer(value)
      } else if (kind === QUESTION_KIND_SINGLE_SELECT) {
        // Single-select: the value is a selected option id, not free text.
        answers[questionId] = selectedAnswer(value)
      } else {
        answers[questionId] = textAnswer(value)
      }
    }
    return {requestId, response: 'accept', answers}
  }

  return null
}

/**
 * AHP-native source for the interactive prompt composer. Reads pending
 * {@link ChatInputRequest}s from the shared relay's `ChatState` and projects
 * them onto {@link NormalizedPrompt}s, mirroring the `EventsPromptState` shape
 * so it can be swapped in wherever the events/logs source is used.
 */
export function useAhpPromptState({
  taskId,
  environmentId,
  sessionId,
  enabled,
}: UseAhpPromptStateOptions): AhpPromptState {
  const {state} = useAhpSessionState({taskId, environmentId, sessionId, enabled})
  const [resolvedRequestIds, setResolvedRequestIds] = useState<Set<string>>(() => new Set())
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null)

  const requestsById = useMemo(() => {
    const map = new Map<string, ChatInputRequest>()
    if (state) for (const request of getInputRequests(state)) map.set(request.id, request)
    return map
  }, [state])

  const pendingPrompts = useMemo(() => {
    if (!state) return []
    const prompts: NormalizedPrompt[] = []
    for (const request of getInputRequests(state)) {
      if (resolvedRequestIds.has(request.id)) continue
      const prompt = ahpInputRequestToPrompt(request)
      if (prompt) prompts.push(prompt)
    }
    return prompts
  }, [state, resolvedRequestIds])

  const isLoading = enabled && !state

  const selectedIndex = useMemo(() => {
    if (!pendingPrompts.length) return -1
    if (!selectedRequestId) return 0
    const index = pendingPrompts.findIndex(prompt => prompt.toolCallId === selectedRequestId)
    return index === -1 ? 0 : index
  }, [pendingPrompts, selectedRequestId])

  const visiblePendingPrompt = isLoading ? null : (pendingPrompts[selectedIndex] ?? null)

  const markPromptResolved = useCallback((requestId: string) => {
    setResolvedRequestIds(prev => new Set(prev).add(requestId))
  }, [])

  const unmarkPromptResolved = useCallback((requestId: string) => {
    setResolvedRequestIds(prev => {
      if (!prev.has(requestId)) return prev
      const next = new Set(prev)
      next.delete(requestId)
      return next
    })
  }, [])

  const dismissPrompt = useCallback(() => {
    if (visiblePendingPrompt) {
      setResolvedRequestIds(prev => new Set(prev).add(visiblePendingPrompt.toolCallId))
    }
  }, [visiblePendingPrompt])

  const goToPreviousPrompt = useCallback(() => {
    setSelectedRequestId(prev => {
      const currentIndex = prev ? pendingPrompts.findIndex(p => p.toolCallId === prev) : selectedIndex
      const nextIndex = Math.max(0, currentIndex - 1)
      return pendingPrompts[nextIndex]?.toolCallId ?? null
    })
  }, [pendingPrompts, selectedIndex])

  const goToNextPrompt = useCallback(() => {
    setSelectedRequestId(prev => {
      const currentIndex = prev ? pendingPrompts.findIndex(p => p.toolCallId === prev) : selectedIndex
      const nextIndex = Math.min(pendingPrompts.length - 1, currentIndex + 1)
      return pendingPrompts[nextIndex]?.toolCallId ?? null
    })
  }, [pendingPrompts, selectedIndex])

  const respond = useCallback(
    (toolCallId: string, response: unknown, commandType: CommandType) => {
      // ask_user / elicitation: answer with chat/inputCompleted.
      const request = requestsById.get(toolCallId)
      if (!request) return
      const params = ahpCompletionFromResponse(request, response, commandType)
      if (!params) return
      dispatchRelayInputCompleted(taskId, params)
    },
    [requestsById, taskId],
  )

  return {
    pendingPrompt: visiblePendingPrompt,
    pendingPromptIndex: visiblePendingPrompt ? selectedIndex : -1,
    pendingPromptCount: pendingPrompts.length,
    isLoading,
    isExpired: false,
    markPromptResolved,
    unmarkPromptResolved,
    dismissPrompt,
    goToPreviousPrompt,
    goToNextPrompt,
    respond,
  }
}
